<div data-spartan-page class="p-6 md:p-8 space-y-6" style="background: rgb(var(--ds-bg, 250 250 249)); min-height: 100%;">

  <!-- Page Header -->
  <app-page-header
    eyebrow="Shopee · Repack"
    title="Update Carton and Parcels"
    subtitle="Update carton dimensions and add or remove parcels from this carton."
  />

  <!-- Feedback alerts -->
  @if (successMessage) {
    <app-inline-alert tone="success" [message]="successMessage" />
  }
  @if (errorMessage) {
    <app-inline-alert tone="danger" [message]="errorMessage" />
  }

  <!-- Loading state -->
  @if (isLoading) {
    <div class="flex flex-col items-center justify-center py-16 gap-3">
      <div class="h-8 w-8 rounded-full border-2 border-accent border-t-transparent animate-spin"></div>
      <p class="text-sm text-muted-foreground">Loading carton details…</p>
    </div>
  }

  @if (orderDetail && !isLoading) {

    <!-- Carton Info Card -->
    <div hlmCard>
      <div class="px-4 py-3 border-b border-border">
        <h2 class="text-sm font-semibold text-foreground">Carton Information</h2>
      </div>
      <div class="p-4">
        <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-8 gap-y-4 mb-4">
          <div>
            <p class="text-[10.5px] font-medium uppercase tracking-wider text-muted-foreground mb-1">Carton Number</p>
            <p class="font-mono text-sm font-medium text-foreground">{{ orderDetail.cartonNo || 'N/A' }}</p>
          </div>
          <div>
            <p class="text-[10.5px] font-medium uppercase tracking-wider text-muted-foreground mb-1">ILH Shopee No</p>
            <p class="font-mono text-sm text-foreground">{{ orderDetail.shopeeOrderNo || 'N/A' }}</p>
          </div>
          <div>
            <p class="text-[10.5px] font-medium uppercase tracking-wider text-muted-foreground mb-1">Status</p>
            <span class="inline-flex items-center rounded px-2 py-0.5 text-[11px] font-medium"
              [ngClass]="{
                'bg-green-50 text-green-700 border border-green-200 dark:bg-green-500/15 dark:text-green-300': orderDetail.status === 'Completed',
                'bg-amber-50 text-amber-700 border border-amber-200 dark:bg-amber-500/15 dark:text-amber-300': orderDetail.status === 'Repacked',
                'bg-red-50 text-red-700 border border-red-200 dark:bg-red-500/15 dark:text-red-300': orderDetail.status === 'Cancelled',
                'bg-blue-50 text-blue-700 border border-blue-200 dark:bg-blue-500/15 dark:text-blue-300': !['Completed','Repacked','Cancelled'].includes(orderDetail.status || '')
              }">
              {{ orderDetail.status || 'N/A' }}
            </span>
          </div>
          <div>
            <p class="text-[10.5px] font-medium uppercase tracking-wider text-muted-foreground mb-1">Service Code</p>
            <p class="text-sm text-foreground">{{ orderDetail.serviceCode || 'N/A' }}</p>
          </div>
          <div>
            <p class="text-[10.5px] font-medium uppercase tracking-wider text-muted-foreground mb-1">Destination</p>
            <p class="text-sm text-foreground">{{ orderDetail.destinationRegion || 'N/A' }}</p>
          </div>
        </div>

        <!-- Original dimensions reference -->
        <div class="rounded-lg bg-surface-raised border border-border p-3 mb-4">
          <p class="font-medium text-sm text-foreground mb-2">Original Carton Dimensions (Reference)</p>
          <div class="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div>
              <p class="text-xs text-muted-foreground mb-1">Weight</p>
              <p class="font-medium text-sm text-foreground">{{ originalMeasurements.weight }} kg</p>
            </div>
            <div class="col-span-2">
              <p class="text-xs text-muted-foreground mb-1">Dimensions (L×W×H)</p>
              <p class="font-mono font-medium text-sm text-foreground">{{ originalMeasurements.length }} × {{ originalMeasurements.width }} × {{ originalMeasurements.height }} cm</p>
            </div>
            <div>
              <p class="text-xs text-muted-foreground mb-1">Volume</p>
              <p class="font-medium text-sm text-foreground">{{ originalMeasurements.volume }} cm³</p>
            </div>
          </div>
          <button type="button" hlmBtn variant="outline" size="sm" class="mt-3 h-7 text-xs"
            (click)="updateRequest.cartonWeight = originalMeasurements.weight;
                     updateRequest.cartonLength = originalMeasurements.length;
                     updateRequest.cartonWidth = originalMeasurements.width;
                     updateRequest.cartonHeight = originalMeasurements.height;
                     calculateVolume()">
            <ng-icon hlm name="lucideClipboard" class="mr-1.5 size-3.5" />
            Copy Original Dimensions
          </button>
        </div>

        <!-- New Dimensions form -->
        <p class="font-semibold text-sm text-foreground mb-3">New Carton Dimensions</p>
        <form #dimensionsForm="ngForm">
          <div class="grid grid-cols-1 sm:grid-cols-4 gap-3 mb-3">
            <div>
              <label for="cartonWeight" class="block text-xs font-medium text-muted-foreground mb-1">Weight (kg) <span class="text-red-500">*</span></label>
              <input type="number" id="cartonWeight" name="cartonWeight"
                [(ngModel)]="updateRequest.cartonWeight" (change)="calculateVolume()"
                step="0.01" required
                class="w-full h-9 border border-border-strong bg-surface rounded-md px-3 text-sm focus-visible:ring-2 focus-visible:ring-accent outline-none transition" />
            </div>
            <div class="sm:col-span-3">
              <label class="block text-xs font-medium text-muted-foreground mb-1">Dimensions (cm) <span class="text-red-500">*</span></label>
              <div class="grid grid-cols-3 gap-2">
                <div class="flex">
                  <span class="inline-flex items-center px-2.5 h-9 rounded-l-md border border-r-0 border-border-strong bg-surface-raised text-sm text-muted-foreground">L</span>
                  <input type="number" id="cartonLength" name="cartonLength"
                    [(ngModel)]="updateRequest.cartonLength" (change)="calculateVolume()" step="0.1" required
                    class="flex-1 min-w-0 h-9 border border-border-strong bg-surface rounded-r-md px-3 text-sm focus-visible:ring-2 focus-visible:ring-accent outline-none transition" />
                </div>
                <div class="flex">
                  <span class="inline-flex items-center px-2.5 h-9 rounded-l-md border border-r-0 border-border-strong bg-surface-raised text-sm text-muted-foreground">W</span>
                  <input type="number" id="cartonWidth" name="cartonWidth"
                    [(ngModel)]="updateRequest.cartonWidth" (change)="calculateVolume()" step="0.1" required
                    class="flex-1 min-w-0 h-9 border border-border-strong bg-surface rounded-r-md px-3 text-sm focus-visible:ring-2 focus-visible:ring-accent outline-none transition" />
                </div>
                <div class="flex">
                  <span class="inline-flex items-center px-2.5 h-9 rounded-l-md border border-r-0 border-border-strong bg-surface-raised text-sm text-muted-foreground">H</span>
                  <input type="number" id="cartonHeight" name="cartonHeight"
                    [(ngModel)]="updateRequest.cartonHeight" (change)="calculateVolume()" step="0.1" required
                    class="flex-1 min-w-0 h-9 border border-border-strong bg-surface rounded-r-md px-3 text-sm focus-visible:ring-2 focus-visible:ring-accent outline-none transition" />
                </div>
              </div>
            </div>
          </div>
          <div class="mb-4 max-w-xs">
            <label for="cartonVolume" class="block text-xs font-medium text-muted-foreground mb-1">Volume (cm³)</label>
            <input type="number" id="cartonVolume" name="cartonVolume"
              [(ngModel)]="updateRequest.cartonVolume" readonly
              class="w-full h-9 border border-border bg-surface-raised rounded-md px-3 text-sm text-muted-foreground outline-none cursor-not-allowed" />
            <p class="mt-1 text-[11px] text-muted-foreground">Auto-calculated</p>
          </div>
          <button type="button" hlmBtn variant="outline" size="sm"
            (click)="updateDimensions()">
            Update Dimensions Only
          </button>
        </form>
      </div>
    </div>

    <!-- Transfer-list: two columns -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">

      <!-- Current Parcels in Carton -->
      <div hlmCard>
        <div class="px-4 py-3 border-b border-border flex items-center justify-between">
          <h2 class="text-sm font-semibold text-foreground">Current Parcels in Carton</h2>
          <span class="inline-flex items-center rounded px-2 py-0.5 text-[11px] font-medium bg-surface-raised text-muted-foreground border border-border">
            {{ currentParcels.length }}
          </span>
        </div>
        <div class="overflow-x-auto">
          <table class="w-full text-sm">
            <thead>
              <tr class="text-[11px] font-medium uppercase tracking-wider text-muted-foreground border-b border-border-strong">
                <th class="text-left px-4 py-2.5 font-medium w-11">
                  <input type="checkbox" class="form-check-input w-4 h-4"
                    [checked]="areAllCurrentParcelsSelected()"
                    (change)="toggleAllCurrentParcels($event)" />
                </th>
                <th class="text-left px-4 py-2.5 font-medium">Tracking Number</th>
                <th class="text-left px-4 py-2.5 font-medium">Weight</th>
                <th class="text-left px-4 py-2.5 font-medium">Dimensions</th>
              </tr>
            </thead>
            <tbody>
              @for (parcel of currentParcels; track parcel.trackingNumber || $index) {
                <tr class="h-10 border-b border-border last:border-0 transition-colors"
                  [class.bg-blue-50]="selectedParcelsToRemove.includes(parcel.trackingNumber)">
                  <td class="px-4">
                    <input type="checkbox" class="form-check-input w-4 h-4"
                      [checked]="selectedParcelsToRemove.includes(parcel.trackingNumber)"
                      (change)="toggleRemoveParcel(parcel.trackingNumber)" />
                  </td>
                  <td class="px-4 font-mono text-[12.5px] text-foreground">{{ parcel.trackingNumber }}</td>
                  <td class="px-4 text-muted-foreground text-xs">{{ parcel.weight || 'N/A' }} kg</td>
                  <td class="px-4 text-muted-foreground text-xs">
                    {{ parcel.length || '—' }} × {{ parcel.width || '—' }} × {{ parcel.height || '—' }}
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>
        <div class="px-4 py-3 border-t border-border">
          <button hlmBtn variant="outline" size="sm"
            class="text-red-600 border-red-200 hover:bg-red-50 dark:hover:bg-red-900/20"
            [disabled]="selectedParcelsToRemove.length === 0"
            (click)="removeSelectedParcels()">
            <ng-icon hlm name="lucideMinus" class="mr-1.5 size-3.5" />
            Remove Selected ({{ selectedParcelsToRemove.length }})
          </button>
        </div>
      </div>

      <!-- Available to Add -->
      <div hlmCard>
        <div class="px-4 py-3 border-b border-border flex items-center justify-between gap-2 flex-wrap">
          <div class="flex items-center gap-2">
            <h2 class="text-sm font-semibold text-foreground">Available to Add</h2>
            <span class="inline-flex items-center rounded px-2 py-0.5 text-[11px] font-medium bg-surface-raised text-muted-foreground border border-border">
              {{ availableParcels.length }}
            </span>
          </div>
          <input type="text" placeholder="Search tracking..."
            [(ngModel)]="searchTerm" (input)="filterAvailableParcels()"
            class="h-8 border border-border-strong bg-surface rounded-md px-2.5 text-xs focus-visible:ring-2 focus-visible:ring-accent outline-none w-40" />
        </div>

        @if (isLoadingParcels) {
          <div class="flex flex-col items-center justify-center py-10 gap-2">
            <div class="h-6 w-6 rounded-full border-2 border-accent border-t-transparent animate-spin"></div>
            <p class="text-sm text-muted-foreground">Loading available parcels…</p>
          </div>
        }
        @if (!isLoadingParcels && filteredAvailableParcels.length === 0) {
          <div class="flex flex-col items-center justify-center py-10 gap-2">
            <span class="text-3xl text-border">⬜</span>
            <p class="text-sm text-muted-foreground">No compatible parcels available</p>
          </div>
        }
        @if (!isLoadingParcels && filteredAvailableParcels.length > 0) {
          <div class="overflow-x-auto">
            <table class="w-full text-sm">
              <thead>
                <tr class="text-[11px] font-medium uppercase tracking-wider text-muted-foreground border-b border-border-strong">
                  <th class="text-left px-4 py-2.5 font-medium w-11">
                    <input type="checkbox" class="form-check-input w-4 h-4"
                      [checked]="areAllAvailableParcelsSelected()"
                      (change)="toggleAllAvailableParcels($event)" />
                  </th>
                  <th class="text-left px-4 py-2.5 font-medium">Tracking Number</th>
                  <th class="text-left px-4 py-2.5 font-medium">Location</th>
                  <th class="text-left px-4 py-2.5 font-medium">Weight</th>
                </tr>
              </thead>
              <tbody>
                @for (parcel of filteredAvailableParcels; track parcel.trackingNumber || $index) {
                  <tr class="h-10 border-b border-border last:border-0 transition-colors"
                    [class.bg-blue-50]="selectedParcelsToAdd.includes(parcel.trackingNumber)">
                    <td class="px-4">
                      <input type="checkbox" class="form-check-input w-4 h-4"
                        [checked]="selectedParcelsToAdd.includes(parcel.trackingNumber)"
                        (change)="toggleAddParcel(parcel.trackingNumber)" />
                    </td>
                    <td class="px-4 font-mono text-[12.5px] text-foreground">{{ parcel.trackingNumber }}</td>
                    <td class="px-4 text-muted-foreground text-xs">{{ parcel.shopeeOrderNo || 'Unassociated' }}</td>
                    <td class="px-4 text-muted-foreground text-xs">{{ parcel.weight || 'N/A' }} kg</td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
          <div class="px-4 py-3 border-t border-border">
            <button hlmBtn size="sm"
              [disabled]="selectedParcelsToAdd.length === 0"
              (click)="addSelectedParcels()">
              <ng-icon hlm name="lucidePlus" class="mr-1.5 size-3.5" />
              Add Selected ({{ selectedParcelsToAdd.length }})
            </button>
          </div>
        }
      </div>
    </div>

    <!-- Sticky footer action bar -->
    <div class="sticky bottom-0 bg-background border-t border-border flex items-center justify-between gap-2 px-4 py-3">
      <button type="button" hlmBtn variant="outline" size="sm" (click)="goBack()">
        <ng-icon hlm name="lucideArrowLeft" class="mr-1.5 size-3.5" />
        Back to Orders
      </button>
      <button type="button" hlmBtn size="sm"
        [disabled]="isSubmitting || (!hasParcelChanges() && !hasDimensionChanges())"
        (click)="submitAllChanges()">
        @if (isSubmitting) {
          <span class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
        }
        Update Carton with All Changes
      </button>
    </div>

  }
</div>
